import pageAdd from '@assets/verification/page-add.svg';
import page from '@assets/verification/page.svg';
import scan from '@assets/verification/scan.svg';
import MarkdownWrapper from '@components/ui/markdown-wrapper';
import { DEFAULT_ORG, NSDC_ORG, WES_ORG } from '@global/constants/general';
import { School } from '@mui/icons-material';
import { Box, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles';

interface AlertComponentsProps {
  alert: {
    componentKey: string,
    severity?: string,
    showUponInvisible?: boolean,
    documentUrl?: {
      letterOfAuthorization?: string | Record<string, string>,
      conferralForm?: string | Record<string, string>,
      [key: string]: string | Record<string, string> | undefined
    }
  };
}

const degreeInstructions = (marksheetRequired = false) => {
  const instructions: { [key: string]: string | ReactElement }[] = [
    {
      icon: scan,
      text: 'Please ensure the scans are clear, without shadows, smudges, or distortions, and that the registration/roll number is clearly visible.'
    },
    {
      icon: page,
      text: '<strong>Final Degree Certificates are mandatory for all applicants.</strong> A Provisional Certificate is accepted only if you completed your course within the last 12 months. Applications with incorrect or incomplete documents may be rejected or delayed.'
    },
    {
      icon: pageAdd,
      text: `Additional documents for specific courses: 
      <ul>
      <li><strong>Medical courses:</strong> You must include your Internship Completion Certificate (merge this with your degree certificate or marksheets into a single PDF).</li>
     <li><strong>Nursing courses:</strong> You must include a transcript (<strong>Clinical Hour Transcript</strong>) issued by the college (merge this with your marksheets into a single PDF).</li>
      </ul>`
    }
  ];
  if (!marksheetRequired) {
    instructions.push({
      component: <School sx={{ fill: '#ffffff' }} />,
      text: 'If degree is not available, upload any other supporting documents.'
    });
  }
  return instructions;
};

export default function AlertComponents({ alert }: AlertComponentsProps) {
  const { componentKey } = alert;
  const { txnInfo } = useSelector((s: RootState) => s.formData);
  const { currentVerification } = useSelector((s: RootState) => s.misc);
  const marksheetRequired = txnInfo?.verificationList?.[currentVerification]?.marksheetRequired;
  const getDocumentUrl = (urlValue: string | Record<string, string> | undefined): string => {
    if (!urlValue) return '';
    if (typeof urlValue === 'string') return urlValue;
    return (
      (urlValue as Record<string, string>)?.[
        import.meta.env.VITE_NSDC_PARTNER ? NSDC_ORG : DEFAULT_ORG
      ] ?? ''
    );
  };

  const renderDocumentFormAlert = (
    prefixText: string,
    linkText: string,
    urlValue: string | Record<string, string> | undefined
  ) => {
    const documentUrl = getDocumentUrl(urlValue);
    const hasValidUrl = documentUrl.trim().length > 0;

    return (
      <Box sx={styles.loaWrapper}>
        <List dense>
          <ListItem sx={{ padding: '0 1em', alignItems: 'center' }}>
            <ListItemAvatar sx={styles.listItemAvatar}>
              <img style={{ margin: 'auto' }} src={page as string} alt="Icon" />
            </ListItemAvatar>
            <ListItemText
              sx={{ mt: 0 }}
              primaryTypographyProps={{
                sx: { color: '#ffffff' }
              }}
              primary={
                <Box sx={{ display: 'flex', columnGap: '4px', flexDirection: 'row' }}>
                  <Typography variant="body2" sx={{ color: 'bg.white' }}>
                    {prefixText}&nbsp;
                    {hasValidUrl ? (
                      <a
                        href={documentUrl}
                        style={{ color: '#ffffff', fontWeight: '600' }}
                        target="_blank"
                        rel="noreferrer">
                        {linkText}
                      </a>
                    ) : (
                      <span style={{ color: '#ffffff', fontWeight: '600' }}>{linkText}</span>
                    )}
                    &nbsp; and upload the document below after signing it.
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        </List>
      </Box>
    );
  };

  const getComponent = (component: string) => {
    switch (component) {
      case 'DEGREE':
        return (
          <Box
            sx={{ bgcolor: 'text.primary', borderRadius: '8px', margin: '0 0 1em', width: '100%' }}>
            <List dense>
              {degreeInstructions(marksheetRequired)?.map((item, i) => (
                <ListItem
                  sx={{ padding: '0 1em', alignItems: 'flex-start' }}
                  key={'degreeInstructions-' + i}>
                  <ListItemAvatar sx={styles.listItemAvatar}>
                    {item.icon ? (
                      <img style={{ margin: 'auto' }} src={item.icon as string} alt="Icon" />
                    ) : (
                      <div style={{ margin: '0.8em' }}>{item.component}</div>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ mt: 0 }}
                    primaryTypographyProps={{
                      sx: { color: '#ffffff' }
                    }}
                    primary={<MarkdownWrapper>{item.text as string}</MarkdownWrapper>}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 'LOA':
        return renderDocumentFormAlert(
          'Please fill the',
          'Letter of Authorization form',
          alert?.documentUrl?.letterOfAuthorization
        );

      case 'CF':
        if (import.meta.env.VITE_ORG !== WES_ORG) {
          return null;
        }
        return renderDocumentFormAlert(
          'Please fill the first part for',
          'Doctoral Conferral Form',
          alert?.documentUrl?.conferralForm
        );

      default:
        return null;
    }
  };

  return getComponent(componentKey);
}
